import { useSearchParams } from 'react-router-dom';
import { ORDER_ID_STORAGE_KEY } from '../order-board/catalog.js';
import { PICK_ITEM_ID_STORAGE_KEY } from '../kitchen-display/catalog.js';
import {
  REPORT_RUN_ID_STORAGE_KEY,
  REPORT_RUNS_STORAGE_KEY,
  REVIEW_ID_STORAGE_KEY,
  WEBHOOK_ID_STORAGE_KEY,
} from './catalog.js';

export function useOrderIdParam(): string | null {
  const [params] = useSearchParams();
  return params.get('orderId') ?? params.get('id') ?? getStoredOrderId();
}

export function useReviewIdParam(): string | null {
  const [params] = useSearchParams();
  return params.get('reviewId') ?? params.get('id') ?? getStoredReviewId();
}

export function useWebhookIdParam(): string | null {
  const [params] = useSearchParams();
  return params.get('webhookId') ?? params.get('id') ?? getStoredWebhookId();
}

export function usePickItemIdParam(): string | null {
  const [params] = useSearchParams();
  return params.get('itemId') ?? getStoredPickItemId();
}

export function getStoredOrderId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(ORDER_ID_STORAGE_KEY);
}

export function setStoredOrderId(orderId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(ORDER_ID_STORAGE_KEY, orderId);
}

export function getStoredReviewId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(REVIEW_ID_STORAGE_KEY);
}

export function setStoredReviewId(reviewId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(REVIEW_ID_STORAGE_KEY, reviewId);
}

export function getStoredWebhookId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(WEBHOOK_ID_STORAGE_KEY);
}

export function setStoredWebhookId(webhookId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(WEBHOOK_ID_STORAGE_KEY, webhookId);
}

export function getStoredPickItemId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(PICK_ITEM_ID_STORAGE_KEY);
}

export function setStoredPickItemId(itemId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(PICK_ITEM_ID_STORAGE_KEY, itemId);
}

export function getStoredReportRunId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(REPORT_RUN_ID_STORAGE_KEY);
}

export function setStoredReportRunId(runId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(REPORT_RUN_ID_STORAGE_KEY, runId);
}

export type StoredReportRun = {
  runId: string;
  reportType: string;
  format: string;
  createdAt: string;
  parameters?: Record<string, string>;
};

export function listStoredReportRuns(): StoredReportRun[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(REPORT_RUNS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) {
        return parsed.filter(
          (row): row is StoredReportRun =>
            row != null &&
            typeof row === 'object' &&
            typeof (row as StoredReportRun).runId === 'string',
        );
      }
    }
  } catch {
    /* ignore malformed cache */
  }
  const legacy = getStoredReportRunId();
  if (!legacy) return [];
  return [
    {
      runId: legacy,
      reportType: 'sales-summary',
      format: 'CSV',
      createdAt: new Date().toISOString(),
    },
  ];
}

export function upsertStoredReportRun(run: StoredReportRun): void {
  if (typeof localStorage === 'undefined') return;
  const next = [run, ...listStoredReportRuns().filter((row) => row.runId !== run.runId)].slice(
    0,
    20,
  );
  localStorage.setItem(REPORT_RUNS_STORAGE_KEY, JSON.stringify(next));
  setStoredReportRunId(run.runId);
}

export type ExportCentreRun = StoredReportRun & {
  id?: string;
  state?: string;
  expiresAt?: string | null;
  retentionUntil?: string | null;
  downloadUrl?: string | null;
  expired?: boolean;
};

export function mergeExportCentreRuns(
  tenant: Array<{
    id?: string;
    runId?: string;
    reportType?: string;
    format?: string;
    createdAt?: string;
    state?: string;
    expiresAt?: string | null;
    retentionUntil?: string | null;
    downloadUrl?: string | null;
    expired?: boolean;
    parameters?: Record<string, string>;
  }>,
  stored: StoredReportRun[],
): ExportCentreRun[] {
  const byId = new Map<string, ExportCentreRun>();
  for (const run of stored) {
    byId.set(run.runId, { ...run });
  }
  for (const run of tenant) {
    const runId = run.runId ?? run.id;
    if (!runId) continue;
    const existing = byId.get(runId);
    byId.set(runId, {
      runId,
      reportType: run.reportType ?? existing?.reportType ?? 'sales-summary',
      format: run.format ?? existing?.format ?? 'CSV',
      createdAt: run.createdAt ?? existing?.createdAt ?? new Date().toISOString(),
      parameters: run.parameters ?? existing?.parameters,
      id: run.id ?? runId,
      state: run.state,
      expiresAt: run.expiresAt ?? run.retentionUntil ?? existing?.expiresAt,
      retentionUntil: run.retentionUntil ?? existing?.retentionUntil,
      downloadUrl: run.downloadUrl,
      expired: run.expired,
    });
  }
  return [...byId.values()].sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
}

export function reviewListPath(): string {
  return '/review-list';
}

export function respondToReviewPath(reviewId: string): string {
  return `/respond-to-review?reviewId=${encodeURIComponent(reviewId)}`;
}

export function reportReviewPath(reviewId: string): string {
  return `/report-review?reviewId=${encodeURIComponent(reviewId)}`;
}

export function withReportPeriod(path: string, query: string): string {
  if (!query) return path;
  return `${path}?${query}`;
}

export function tradingDashboardPath(): string {
  return '/trading-dashboard';
}

export function salesReportPath(): string {
  return '/sales-report';
}

export function operationsReportPath(): string {
  return '/operations-report';
}

export function productPerformancePath(): string {
  return '/product-performance';
}

export function exportCentrePath(): string {
  return '/export-centre';
}

export function notificationSettingsPath(): string {
  return '/notification-settings';
}

export function businessSettingsPath(): string {
  return '/business-settings';
}

export function integrationsOverviewPath(): string {
  return '/integrations-overview';
}

export function apiClientsPath(): string {
  return '/api-clients';
}

export function webhooksPath(): string {
  return '/webhooks';
}

export function webhookDeliveriesPath(webhookId: string): string {
  return `/webhook-deliveries?webhookId=${encodeURIComponent(webhookId)}`;
}

export function notificationDrawerPath(): string {
  return '/notification-drawer';
}

export function weighItemPath(orderId: string, itemId?: string): string {
  const base = `/weigh-item?orderId=${encodeURIComponent(orderId)}`;
  return itemId ? `${base}&itemId=${encodeURIComponent(itemId)}` : base;
}

export function weightExceptionsPath(orderId: string): string {
  return `/weight-exceptions?orderId=${encodeURIComponent(orderId)}`;
}

export function returnedOrderPath(orderId: string): string {
  return `/returned-order?orderId=${encodeURIComponent(orderId)}`;
}

export function ageVerificationPath(orderId: string): string {
  return `/age-verification?orderId=${encodeURIComponent(orderId)}`;
}
